import {
  Add as AddIcon,
  AdminPanelSettings as AdminPanelIcon,
  Analytics as AnalyticsIcon,
  VpnKey as ApiKeyIcon,
  Webhook as WebhookIcon,
  VpnLock as SsoIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Storefront as CatalogIcon,
  Chat as ChatIcon,
  People as CommunityIcon,
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  Edit as EditIcon,
  Event as EventIcon,
  MonetizationOn as FinanceIcon,
  Groups as GroupIcon,
  Store as MarketplaceIcon,
  Mic as MicIcon,
  NetworkWifi as NetworkIcon,
  Notifications as NotificationIcon,
  ShoppingCart as OrderIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  PrivacyTip as PrivacyIcon,
  StarBorder as RatingIcon,
  PersonAdd as ReferralIcon,
  Assessment as ReportIcon,
  StarRate as ReviewIcon,
  Security as SecurityIcon,
  Receipt as TaxIcon,
  Settings as SettingsIcon,
  Hub as CrmIcon,
  Storage as StorageIcon,
  Assignment as TaskIcon,
  TrendingUp as TrendingIcon,
  EmojiEvents as TrophyIcon,
  VideoCall as VideoIcon,
} from "@mui/icons-material";
import type { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";

import { useSelector } from "react-redux";
import { useRole } from "../../contexts/RoleContext";
import { useTasksBadge } from "../../hooks/useTasksBadge";
import { useHasAgentContract } from "../../hooks/useHasAgentContract";
import { selectUnreadCount } from "../../store/slices/notificationsSlice";
import { InternalRole } from "../../types/enums/internal-roles.enum";
import {
  CollaborationRole,
  EventRole,
  OrganizerRole,
  RolePermission,
  RolePermissions,
  SystemRole,
  UserContextRole,
} from "../../types/enums/user-roles";
import {
  convertUserRoleToContextRole,
  isAdminRole,
  isOrganizerRole,
  isParticipantRole,
  isSpeakerRole,
  isSupplierRole,
} from "../../utils/role-helpers";

export interface NavigationSubItem {
  title: string;
  path: string;
  icon?: React.ReactNode;
  badge?: number;
  new?: boolean;
  permission?: RolePermission;
}

export interface NavigationItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  children?: NavigationSubItem[];
  divider?: boolean;
  new?: boolean;
  roles?: (EventRole | SystemRole)[];
  permission?: RolePermission;
}

export interface NavigationBlock {
  title: string;
  items: NavigationItem[];
  divider?: boolean;
}

const getFunctionalNavigationBlocks = (
  currentRole: UserContextRole,
  t: TFunction,
  tasksBadgeCount: number,
  notificationsBadge: number,
  chatsBadge: number,
  ordersBadge: number,
  hasAgentContract: boolean,
): NavigationBlock[] => {
  const blocks: NavigationBlock[] = [];
  // PLATFORM_MANAGER = «организатор на платформе»: те же вкладки, что у EventRole.ORGANIZER (без привязки к событию)
  const effectiveRole =
    currentRole === InternalRole.PLATFORM_MANAGER
      ? EventRole.ORGANIZER
      : currentRole;

  blocks.push({
    title: t("navigation.main"),
    items: [
      {
        title: t("navigation.dashboard"),
        path: "/dashboard",
        icon: <DashboardIcon />,
      },
      {
        title: t("navigation.tasks"),
        path: "/tasks",
        icon: <TaskIcon />,
        badge: tasksBadgeCount, // Реальный счетчик из API
      },
    ],
  });

  // PLATFORM_MANAGER: как у организатора (см. effectiveRole ниже), иначе в getEventsNavigationItems
  // для внутренних ролей нет case — меню «События» / «Создать мероприятие» пустое.
  const eventsNavigationRole =
    currentRole === InternalRole.PLATFORM_MANAGER
      ? EventRole.ORGANIZER
      : currentRole;
  const eventsItems = getEventsNavigationItems(
    eventsNavigationRole,
    t,
    tasksBadgeCount,
  );
  if (eventsItems.length > 0) {
    blocks.push({
      title: t("navigation.events"),
      items: eventsItems,
    });
  }

  const marketplaceItems = getMarketplaceNavigationItems(
    currentRole,
    t,
    ordersBadge,
  );
  if (marketplaceItems.length > 0) {
    blocks.push({
      title: t("navigation.marketplace"),
      items: marketplaceItems,
    });
  }

  const communityItems = getCommunityNavigationItems(
    effectiveRole,
    t,
    notificationsBadge,
    chatsBadge,
    hasAgentContract,
  );
  if (communityItems.length > 0) {
    blocks.push({
      title: t("navigation.community"),
      items: communityItems,
    });
  }

  // Третьим аргументом идёт НАСТОЯЩАЯ роль: финансовое меню строится по
  // `effectiveRole` (PLATFORM_MANAGER показан как организатор), но права на
  // платформенные разделы даёт именно платформенная роль (AD-467).
  const financeItems = getFinanceNavigationItems(effectiveRole, t, currentRole);
  if (financeItems.length > 0) {
    blocks.push({
      title: t("navigation.finance"),
      items: financeItems,
    });
  }

  const analyticsItems = getAnalyticsNavigationItems(effectiveRole, t);
  if (analyticsItems.length > 0) {
    blocks.push({
      title: t("navigation.analytics"),
      items: analyticsItems,
    });
  }

  const documentsItems = getDocumentsNavigationItems(effectiveRole, t);
  if (documentsItems.length > 0) {
    blocks.push({
      title: t("navigation.documents"),
      items: documentsItems,
    });
  }

  blocks.push({
    title: t("navigation.settingsLabel"),
    items: [
      {
        title: t("navigation.settingsLabel"),
        path: "/settings",
        icon: <SettingsIcon />,
        children: [
          {
            title: t("navigation.general"),
            path: "/settings",
            icon: <SettingsIcon />,
          },
          {
            title: t("navigation.notifications"),
            path: "/settings/notifications",
            icon: <NotificationIcon />,
          },
          {
            title: t("navigation.security"),
            path: "/settings/security",
            icon: <SecurityIcon />,
          },
          // AD-217: согласия и удаление учётки по GDPR — право должно быть
          // находимым из меню, а не только по прямому адресу.
          {
            title: t("navigation.privacySettings"),
            path: "/settings/privacy",
            icon: <PrivacyIcon />,
          },
          {
            title: t("navigation.profile"),
            path: "/settings/profile",
            icon: <PersonIcon />,
          },
          {
            title: t("navigation.subscription"),
            path: "/settings/subscription",
            icon: <PaymentIcon />,
          },
          {
            title: t("navigation.crmIntegrations"),
            path: "/settings/crm",
            icon: <CrmIcon />,
          },
          {
            title: t("navigation.taxInformation"),
            path: "/settings/tax",
            icon: <TaxIcon />,
          },
          {
            title: t("navigation.apiKeys"),
            path: "/settings/api-keys",
            icon: <ApiKeyIcon />,
          },
          {
            title: t("navigation.webhooks"),
            path: "/settings/webhooks",
            icon: <WebhookIcon />,
          },
        ],
      },
    ],
    divider: true,
  });

  return blocks;
};

const getEventsNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
  tasksBadgeCount: number,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  // Проверяем, является ли роль внутренней (административной)
  const isInternalRole = Object.values(InternalRole).includes(
    currentRole as InternalRole,
  );

  if (isInternalRole) {
    // Для внутренних ролей - специальная навигация
    switch (currentRole) {
      case InternalRole.SUPER_ADMIN:
      case InternalRole.PLATFORM_ADMIN:
        items.push({
          title: t("navigation.administration"),
          path: "/admin",
          icon: <SecurityIcon />,
          children: [
            {
              title: t("navigation.allEvents"),
              path: "/admin/events",
              icon: <EventIcon />,
            },
            {
              title: t("navigation.users"),
              path: "/admin/users",
              icon: <GroupIcon />,
            },
            {
              title: t("navigation.security") || "Безопасность",
              path: "/admin/security",
              icon: <SecurityIcon />,
            },
            {
              // Настройки SSO правят только администраторы платформы: серверные
              // маршруты `auth/sso/configurations` закрыты теми же ролями (AD-416).
              title: t("navigation.sso"),
              path: "/settings/sso",
              icon: <SsoIcon />,
            },
            {
              title: t("navigation.backup") || "Бэкапы",
              path: "/admin/backup",
              icon: <StorageIcon />,
            },
            {
              title: t("navigation.moderation"),
              path: "/admin/moderation",
              icon: <SecurityIcon />,
            },
            {
              title: t("navigation.system"),
              path: "/admin/system",
              icon: <SettingsIcon />,
            },
          ],
        });
        items.push({
          title: t("navigation.events"),
          path: "/organizer",
          icon: <EventIcon />,
          children: [
            {
              title: t("navigation.organizerPanel"),
              path: "/organizer",
              icon: <DashboardIcon />,
            },
            {
              title: t("navigation.createEvent"),
              path: "/events/create",
              icon: <AddIcon />,
              new: true,
            },
            {
              title: t("navigation.myEvents"),
              path: "/events",
              icon: <EventIcon />,
            },
            {
              title: t("navigation.calendar"),
              path: "/events/calendar",
              icon: <CalendarIcon />,
            },
            {
              title: t("navigation.tasks"),
              path: "/tasks",
              icon: <TaskIcon />,
              badge: tasksBadgeCount,
            },
            {
              title: t("navigation.participants"),
              path: "/analytics/participants",
              icon: <GroupIcon />,
            },
            {
              title: t("navigation.popular"),
              path: "/events/popular",
              icon: <TrendingIcon />,
            },
          ],
        });
        break;
    }
  } else if (currentRole === EventRole.CO_ORGANIZER) {
    // Соорганизатор: всё что у организатора, кроме создания мероприятия
    items.push({
      title: t("navigation.events"),
      path: "/organizer",
      icon: <EventIcon />,
      children: [
        {
          title: t("navigation.organizerPanel"),
          path: "/organizer",
          icon: <DashboardIcon />,
        },
        {
          title: t("navigation.myEvents"),
          path: "/events",
          icon: <EventIcon />,
        },
        {
          title: t("navigation.calendar"),
          path: "/events/calendar",
          icon: <CalendarIcon />,
        },
        {
          title: t("navigation.tasks"),
          path: "/tasks",
          icon: <TaskIcon />,
          badge: tasksBadgeCount,
        },
        {
          title: t("navigation.participants"),
          path: "/analytics/participants",
          icon: <GroupIcon />,
        },
        {
          title: t("navigation.popular"),
          path: "/events/popular",
          icon: <TrendingIcon />,
        },
      ],
    });
  } else if (currentRole === EventRole.EVENT_ADMIN) {
    // Администратор события: управление, участники, задачи, QR — без создания и популярных
    items.push({
      title: t("navigation.events"),
      path: "/organizer",
      icon: <EventIcon />,
      children: [
        {
          title: t("navigation.organizerPanel"),
          path: "/organizer",
          icon: <DashboardIcon />,
        },
        {
          title: t("navigation.myEvents"),
          path: "/events",
          icon: <EventIcon />,
        },
        {
          title: t("navigation.calendar"),
          path: "/events/calendar",
          icon: <CalendarIcon />,
        },
        {
          title: t("navigation.tasks"),
          path: "/tasks",
          icon: <TaskIcon />,
          badge: tasksBadgeCount,
        },
        {
          title: t("navigation.participants"),
          path: "/analytics/participants",
          icon: <GroupIcon />,
        },
      ],
    });
  } else {
    // ORGANIZER и все остальные роли (PARTICIPANT, SUPPLIER и т.д.)
    items.push({
      title: t("navigation.events"),
      path: "/organizer",
      icon: <EventIcon />,
      children: [
        {
          title: t("navigation.organizerPanel"),
          path: "/organizer",
          icon: <DashboardIcon />,
        },
        {
          title: t("navigation.createEvent"),
          path: "/events/create",
          icon: <AddIcon />,
          new: true,
        },
        {
          title: t("navigation.myEvents"),
          path: "/events",
          icon: <EventIcon />,
        },
        {
          title: t("navigation.calendar"),
          path: "/events/calendar",
          icon: <CalendarIcon />,
        },
        {
          title: t("navigation.tasks"),
          path: "/tasks",
          icon: <TaskIcon />,
          badge: tasksBadgeCount,
        },
        {
          title: t("navigation.participants"),
          path: "/analytics/participants",
          icon: <GroupIcon />,
        },
        {
          title: t("navigation.popular"),
          path: "/events/popular",
          icon: <TrendingIcon />,
        },
      ],
    });
  }

  return items;
};

const getMarketplaceNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
  ordersBadge: number,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  // Проверяем, является ли роль внутренней (административной)
  const isInternalRole = Object.values(InternalRole).includes(
    currentRole as InternalRole,
  );

  if (isInternalRole) {
    switch (currentRole) {
      case InternalRole.SUPER_ADMIN:
      case InternalRole.PLATFORM_ADMIN:
      case InternalRole.PLATFORM_MANAGER:
        items.push({
          title: t("navigation.marketplace"),
          path: "/marketplace",
          icon: <MarketplaceIcon />,
          children: [
            {
              title: t("navigation.searchContractors"),
              path: "/marketplace/search",
              icon: <MarketplaceIcon />,
            },
            {
              title: t("navigation.orders"),
              path: "/marketplace/orders",
              icon: <OrderIcon />,
              ...(ordersBadge > 0 ? { badge: ordersBadge } : {}),
            },
            {
              title: t("navigation.compareOffers"),
              path: "/marketplace/compare",
              icon: <AnalyticsIcon />,
            },
            {
              title: t("navigation.favoriteSuppliers"),
              path: "/marketplace/favorites",
              icon: <RatingIcon />,
            },
            {
              title: t("navigation.services"),
              path: "/marketplace/services",
              icon: <CatalogIcon />,
            },
          ],
        });
        break;
    }
    if (items.length > 0) {
      return items;
    }
  }

  switch (currentRole) {
    case EventRole.ORGANIZER:
    case EventRole.CO_ORGANIZER:
      items.push({
        title: t("navigation.marketplace"),
        path: "/marketplace",
        icon: <MarketplaceIcon />,
        children: [
          {
            title: t("navigation.searchContractors"),
            path: "/marketplace/search",
            icon: <MarketplaceIcon />,
          },
          {
            title: t("navigation.orders"),
            path: "/marketplace/orders",
            icon: <OrderIcon />,
            ...(ordersBadge > 0 ? { badge: ordersBadge } : {}),
          },
          {
            title: t("navigation.compareOffers"),
            path: "/marketplace/compare",
            icon: <AnalyticsIcon />,
          },
          {
            title: t("navigation.favoriteSuppliers"),
            path: "/marketplace/favorites",
            icon: <RatingIcon />,
          },
          {
            title: t("navigation.services"),
            path: "/marketplace/services",
            icon: <CatalogIcon />,
          },
        ],
      });
      break;

    case EventRole.EVENT_ADMIN:
      items.push({
        title: t("navigation.marketplace"),
        path: "/marketplace",
        icon: <MarketplaceIcon />,
        children: [
          {
            title: t("navigation.searchContractors"),
            path: "/marketplace/search",
            icon: <MarketplaceIcon />,
          },
          {
            title: t("navigation.orders"),
            path: "/marketplace/orders",
            icon: <OrderIcon />,
            ...(ordersBadge > 0 ? { badge: ordersBadge } : {}),
          },
          {
            title: t("navigation.compareOffers"),
            path: "/marketplace/compare",
            icon: <AnalyticsIcon />,
          },
        ],
      });
      break;

    case EventRole.SUPPLIER:
      items.push({
        title: t("navigation.myBusiness"),
        path: "/marketplace/supplier-hub",
        icon: <BusinessIcon />,
        children: [
          {
            title: t("navigation.supplierHub"),
            path: "/marketplace/supplier-hub",
            icon: <DashboardIcon />,
          },
          {
            title: t("navigation.orders"),
            path: "/supplier/orders",
            icon: <OrderIcon />,
            ...(ordersBadge > 0 ? { badge: ordersBadge } : {}),
          },
          {
            title: t("navigation.catalog"),
            path: "/catalog",
            icon: <CatalogIcon />,
          },
          {
            title: t("navigation.addService"),
            path: "/catalog/add",
            icon: <AddIcon />,
            new: true,
          },
        ],
      });
      break;

    case EventRole.PARTICIPANT:
    case SystemRole.USER:
      items.push({
        title: t("navigation.services"),
        path: "/services",
        icon: <CatalogIcon />,
        children: [
          {
            title: t("navigation.catalog"),
            path: "/services",
            icon: <CatalogIcon />,
          },
          {
            title: t("navigation.recommended"),
            path: "/services/recommended",
            icon: <TrendingIcon />,
          },
          {
            title: t("navigation.reviews"),
            path: "/services/reviews",
            icon: <ReviewIcon />,
          },
        ],
      });
      break;
  }

  return items;
};

/** Кому показывается подменю реферальной программы; в нём и пункт выплат. */
const canSeeReferralProgram = (role: UserContextRole): boolean =>
  role === EventRole.ORGANIZER ||
  role === InternalRole.SUPER_ADMIN ||
  role === InternalRole.PLATFORM_ADMIN ||
  role === InternalRole.PLATFORM_MANAGER;

const getCommunityNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
  notificationsBadge: number,
  chatsBadge: number,
  hasAgentContract: boolean,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  const communityChildren: NavigationSubItem[] = [];

  communityChildren.push(
    {
      title: t("navigation.communityOverview"),
      path: "/community",
      icon: <CommunityIcon />,
    },
    {
      title: t("navigation.chats"),
      path: "/chats",
      icon: <ChatIcon />,
      ...(chatsBadge > 0 ? { badge: chatsBadge } : {}),
    },
    {
      title: t("navigation.videoCalls"),
      path: "/video-calls",
      icon: <VideoIcon />,
    },
    {
      title: t("navigation.notifications"),
      path: "/notifications",
      icon: <NotificationIcon />,
      ...(notificationsBadge > 0 ? { badge: notificationsBadge } : {}),
    },
  );

  // Проверяем, является ли роль внутренней административной
  const isInternalAdmin =
    currentRole === InternalRole.SUPER_ADMIN ||
    currentRole === InternalRole.PLATFORM_ADMIN;

  // Для внутренних админов показываем Команду и Партнёров
  if (isInternalAdmin) {
    communityChildren.push(
      { title: t("navigation.team"), path: "/team", icon: <GroupIcon /> },
      {
        title: t("navigation.partners"),
        path: "/partners",
        icon: <BusinessIcon />,
      },
      {
        title: t("navigation.allUsers"),
        path: "/admin/users",
        icon: <GroupIcon />,
      },
    );
  } else {
    switch (currentRole) {
      case EventRole.PARTICIPANT:
      case EventRole.SPEAKER:
        communityChildren.push(
          {
            title: t("navigation.networking"),
            path: "/network",
            icon: <NetworkIcon />,
          },
          {
            title: t("navigation.contacts"),
            path: "/network/contacts",
            icon: <GroupIcon />,
          },
          {
            title: t("navigation.groups"),
            path: "/network/groups",
            icon: <GroupIcon />,
          },
        );
        break;

      case EventRole.ORGANIZER:
      case EventRole.CO_ORGANIZER:
      case EventRole.EVENT_ADMIN:
        communityChildren.push(
          { title: t("navigation.team"), path: "/team", icon: <GroupIcon /> },
          {
            title: t("navigation.partners"),
            path: "/partners",
            icon: <BusinessIcon />,
          },
        );
        break;

      case EventRole.SUPPLIER:
        communityChildren.push(
          {
            title: t("navigation.clients"),
            path: "/clients",
            icon: <GroupIcon />,
          },
          {
            title: t("navigation.reviews"),
            path: "/reviews",
            icon: <ReviewIcon />,
          },
        );
        break;
    }
  }

  items.push({
    title: t("navigation.community"),
    path: "/community",
    icon: <CommunityIcon />,
    children: communityChildren,
  });

  if (canSeeReferralProgram(currentRole)) {
    items.push({
      title: t("navigation.referralProgram"),
      path: "/referral",
      icon: <ReferralIcon />,
      new: true,
      children: [
        {
          title: t("navigation.referralDashboard"),
          path: "/referral",
          icon: <ReferralIcon />,
        },
        {
          title: t("navigation.referralEarnings"),
          path: "/referral/earnings",
          icon: <FinanceIcon />,
        },
        {
          title: t("navigation.referralMyReferrals"),
          path: "/referral/network",
          icon: <GroupIcon />,
        },
        {
          title: t("navigation.referralInviteFriends"),
          path: "/referral/invite",
          icon: <PersonIcon />,
        },
        {
          title: t("navigation.referralPayouts"),
          path: "/referral/payouts",
          icon: <PaymentIcon />,
        },
        {
          title: t("navigation.referralLeaderboard"),
          path: "/referral/leaderboard",
          icon: <TrophyIcon />,
        },
        {
          title: t("navigation.referralSettings"),
          path: "/referral/settings",
          icon: <SettingsIcon />,
        },
        ...(currentRole === InternalRole.SUPER_ADMIN
          ? [
              {
                title: t("navigation.referralAdminPanel"),
                path: "/admin/referrals",
                icon: <AdminPanelIcon />,
              },
            ]
          : []),
      ],
    });
  } else if (hasAgentContract) {
    // AD-790. Договор агента от роли в событии не зависит, а другого пути к нему нет.
    items.push({
      title: t("navigation.referralPayouts"),
      path: "/referral/payouts",
      icon: <PaymentIcon />,
    });
  }

  return items;
};

const getFinanceNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
  realRole: UserContextRole = currentRole,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  // Проверяем, является ли роль внутренней административной
  const isInternalAdmin =
    currentRole === InternalRole.SUPER_ADMIN ||
    currentRole === InternalRole.PLATFORM_ADMIN;

  // Для внутренних админов показываем полный финансовый раздел
  if (isInternalAdmin) {
    items.push({
      title: t("navigation.finance"),
      path: "/finance",
      icon: <FinanceIcon />,
      children: [
        {
          title: t("navigation.generalAnalytics"),
          path: "/finance/analytics",
          icon: <AnalyticsIcon />,
        },
        {
          title: t("navigation.budgets"),
          path: "/finance/budget",
          icon: <FinanceIcon />,
        },
        {
          title: t("navigation.payments"),
          path: "/finance/payments",
          icon: <PaymentIcon />,
        },
        {
          title: t("navigation.commissions"),
          path: "/finance/commissions",
          icon: <FinanceIcon />,
        },
        {
          title: t("navigation.transactions"),
          path: "/finance/transactions",
          icon: <PaymentIcon />,
        },
        {
          title: t("navigation.reports"),
          path: "/finance/reports",
          icon: <ReportIcon />,
        },
      ],
    });
    return items;
  }

  switch (currentRole) {
    case EventRole.ORGANIZER:
    case EventRole.CO_ORGANIZER:
    case EventRole.EVENT_ADMIN:
      // AD-467: у событийных ролей «Бюджеты» и «Платежи» отсюда сняты — сервер
      // держит и планировщик (`finance/budget`), и списки платежей под
      // платформенными ролями, так что пункты вели в отказ. Остаются те, что
      // событийным ролям сервер открывает: отчёты и счета
      // (@EventOrInternalRoles по своему событию). PLATFORM_MANAGER попадает в
      // эту же ветку (выше он показан как организатор), но его платформенная
      // роль в @InternalRoles есть — ему оба пункта остаются.
      items.push({
        title: t("navigation.finance"),
        path: "/finance",
        icon: <FinanceIcon />,
        children: [
          ...(realRole === InternalRole.PLATFORM_MANAGER
            ? [
                {
                  title: t("navigation.budgets"),
                  path: "/finance/budget",
                  icon: <FinanceIcon />,
                },
                {
                  title: t("navigation.payments"),
                  path: "/finance/payments",
                  icon: <PaymentIcon />,
                },
              ]
            : []),
          {
            title: t("navigation.reports"),
            path: "/finance/reports",
            icon: <ReportIcon />,
          },
          {
            title: t("navigation.invoices"),
            path: "/finance/invoices",
            icon: <DocumentIcon />,
          },
        ],
      });
      break;

    case EventRole.SUPPLIER:
      items.push({
        title: t("navigation.finance"),
        path: "/finance",
        icon: <FinanceIcon />,
        children: [
          {
            title: t("navigation.income"),
            path: "/finance/income",
            icon: <FinanceIcon />,
          },
          {
            title: t("navigation.payments"),
            path: "/finance/payments",
            icon: <PaymentIcon />,
          },
          {
            title: t("navigation.salesAnalytics"),
            path: "/finance/analytics",
            icon: <TrendingIcon />,
          },
          {
            title: t("navigation.taxes"),
            path: "/finance/taxes",
            icon: <DocumentIcon />,
          },
        ],
      });
      break;

    case EventRole.SPEAKER:
      items.push({
        title: t("navigation.finance"),
        path: "/finance",
        icon: <FinanceIcon />,
        children: [
          {
            title: t("navigation.fees"),
            path: "/finance/fees",
            icon: <FinanceIcon />,
          },
          {
            title: t("navigation.payouts"),
            path: "/finance/payouts",
            icon: <PaymentIcon />,
          },
          {
            title: t("navigation.contracts"),
            path: "/finance/contracts",
            icon: <DocumentIcon />,
          },
        ],
      });
      break;

    case SystemRole.ADMIN:
      items.push({
        title: t("navigation.finance"),
        path: "/finance",
        icon: <FinanceIcon />,
        children: [
          {
            title: t("navigation.generalAnalytics"),
            path: "/finance/analytics",
            icon: <AnalyticsIcon />,
          },
          {
            title: t("navigation.commissions"),
            path: "/finance/commissions",
            icon: <FinanceIcon />,
          },
          {
            title: t("navigation.transactions"),
            path: "/finance/transactions",
            icon: <PaymentIcon />,
          },
        ],
      });
      break;
  }

  return items;
};

const getAnalyticsNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  // SUPER_ADMIN и PLATFORM_ADMIN: полная системная аналитика
  const isFullAdmin = [
    InternalRole.SUPER_ADMIN,
    InternalRole.PLATFORM_ADMIN,
  ].includes(currentRole as InternalRole);

  if (isFullAdmin) {
    items.push({
      title: t("navigation.systemAnalytics"),
      path: "/analytics",
      icon: <AnalyticsIcon />,
      children: [
        {
          title: t("navigation.analyticsDashboard"),
          path: "/analytics",
          icon: <AnalyticsIcon />,
        },
        {
          title: t("navigation.events"),
          path: "/analytics/events",
          icon: <EventIcon />,
        },
        {
          title: t("navigation.participants"),
          path: "/analytics/participants",
          icon: <GroupIcon />,
        },
        {
          title: t("navigation.users"),
          path: "/analytics/users",
          icon: <PersonIcon />,
        },
        {
          title: t("navigation.conversions"),
          path: "/analytics/conversions",
          icon: <TrendingIcon />,
        },
        {
          title: t("navigation.systemMetrics"),
          path: "/monitoring",
          icon: <AnalyticsIcon />,
        },
      ],
    });
    return items;
  }

  // PLATFORM_MANAGER и прочие внутренние роли: без системных разделов users/conversions/monitoring
  const isInternalStaff = [
    InternalRole.PLATFORM_MANAGER,
    InternalRole.MONITORING_ENGINEER,
    InternalRole.OPERATIONS_MANAGER,
    InternalRole.TECHNICAL_SUPPORT,
    InternalRole.SECURITY_ADMIN,
    InternalRole.DEVELOPER,
  ].includes(currentRole as InternalRole);

  if (isInternalStaff) {
    items.push({
      title: t("navigation.systemAnalytics"),
      path: "/analytics",
      icon: <AnalyticsIcon />,
      children: [
        {
          title: t("navigation.analyticsDashboard"),
          path: "/analytics",
          icon: <AnalyticsIcon />,
        },
        {
          title: t("navigation.events"),
          path: "/analytics/events",
          icon: <EventIcon />,
        },
        {
          title: t("navigation.participants"),
          path: "/analytics/participants",
          icon: <GroupIcon />,
        },
      ],
    });
    return items;
  }

  switch (currentRole) {
    case EventRole.ORGANIZER:
      items.push({
        title: t("navigation.analytics"),
        path: "/analytics",
        icon: <AnalyticsIcon />,
        children: [
          {
            title: t("navigation.analyticsDashboard"),
            path: "/analytics",
            icon: <AnalyticsIcon />,
          },
          {
            title: t("navigation.events"),
            path: "/analytics/events",
            icon: <EventIcon />,
          },
          {
            title: t("navigation.participants"),
            path: "/analytics/participants",
            icon: <GroupIcon />,
          },
        ],
      });
      break;

    case EventRole.CO_ORGANIZER:
    case EventRole.EVENT_ADMIN:
      items.push({
        title: t("navigation.analytics"),
        path: "/analytics",
        icon: <AnalyticsIcon />,
        children: [
          {
            title: t("navigation.analyticsDashboard"),
            path: "/analytics",
            icon: <AnalyticsIcon />,
          },
          {
            title: t("navigation.events"),
            path: "/analytics/events",
            icon: <EventIcon />,
          },
          {
            title: t("navigation.participants"),
            path: "/analytics/participants",
            icon: <GroupIcon />,
          },
        ],
      });
      break;

    case EventRole.SUPPLIER:
      items.push({
        title: t("navigation.analytics"),
        path: "/analytics",
        icon: <AnalyticsIcon />,
        children: [
          {
            title: t("navigation.salesAnalytics"),
            path: "/analytics/sales",
            icon: <TrendingIcon />,
          },
          {
            title: t("navigation.clients"),
            path: "/analytics/clients",
            icon: <GroupIcon />,
          },
          {
            title: t("navigation.services"),
            path: "/analytics/services",
            icon: <CatalogIcon />,
          },
          {
            title: t("navigation.ratings"),
            path: "/analytics/ratings",
            icon: <ReviewIcon />,
          },
        ],
      });
      break;

    case EventRole.SPEAKER:
      items.push({
        title: t("navigation.statistics"),
        path: "/speaker-stats",
        icon: <AnalyticsIcon />,
        children: [
          {
            title: t("navigation.presentations"),
            path: "/speaker-stats/presentations",
            icon: <MicIcon />,
          },
          {
            title: t("navigation.feedback"),
            path: "/speaker-stats/feedback",
            icon: <ReviewIcon />,
          },
          {
            title: t("navigation.ratings"),
            path: "/speaker-stats/ratings",
            icon: <RatingIcon />,
          },
        ],
      });
      break;

    case SystemRole.ADMIN:
      items.push({
        title: t("navigation.systemAnalytics"),
        path: "/analytics",
        icon: <AnalyticsIcon />,
        children: [
          {
            title: t("navigation.analyticsDashboard"),
            path: "/analytics",
            icon: <AnalyticsIcon />,
          },
          {
            title: t("navigation.events"),
            path: "/analytics/events",
            icon: <EventIcon />,
          },
          {
            title: t("navigation.participants"),
            path: "/analytics/participants",
            icon: <GroupIcon />,
          },
          {
            title: t("navigation.users"),
            path: "/analytics/users",
            icon: <PersonIcon />,
          },
          {
            title: t("navigation.conversions"),
            path: "/analytics/conversions",
            icon: <TrendingIcon />,
          },
        ],
      });
      break;
  }

  return items;
};

const getDocumentsNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  // Проверяем, является ли роль внутренней административной
  const isInternalAdmin =
    currentRole === InternalRole.SUPER_ADMIN ||
    currentRole === InternalRole.PLATFORM_ADMIN;

  // Для внутренних админов показываем административные документы
  if (isInternalAdmin) {
    items.push({
      title: t("navigation.documents"),
      path: "/documents",
      icon: <DocumentIcon />,
      children: [
        {
          title: t("navigation.allDocuments"),
          path: "/documents",
          icon: <DocumentIcon />,
        },
        {
          title: t("navigation.contracts"),
          path: "/documents/contracts",
          icon: <DocumentIcon />,
        },
        {
          title: t("navigation.templates"),
          path: "/documents/templates",
          icon: <DocumentIcon />,
        },
        {
          title: t("navigation.reports"),
          path: "/documents/reports",
          icon: <ReportIcon />,
        },
      ],
    });
    return items;
  }

  switch (currentRole) {
    case EventRole.ORGANIZER:
    case EventRole.CO_ORGANIZER:
    case EventRole.EVENT_ADMIN:
      items.push({
        title: t("navigation.documents"),
        path: "/documents",
        icon: <DocumentIcon />,
        children: [
          {
            title: t("navigation.contracts"),
            path: "/documents/contracts",
            icon: <DocumentIcon />,
          },
          {
            title: t("navigation.invoices"),
            path: "/documents/invoices",
            icon: <DocumentIcon />,
          },
          {
            title: t("navigation.reports"),
            path: "/documents/reports",
            icon: <ReportIcon />,
          },
          {
            title: t("navigation.templates"),
            path: "/documents/templates",
            icon: <DocumentIcon />,
          },
        ],
      });
      break;

    case EventRole.SUPPLIER:
      items.push({
        title: t("navigation.documents"),
        path: "/documents",
        icon: <DocumentIcon />,
        children: [
          {
            title: t("navigation.contracts"),
            path: "/documents/contracts",
            icon: <DocumentIcon />,
          },
        ],
      });
      break;

    case SystemRole.ADMIN:
      items.push({
        title: t("navigation.documents"),
        path: "/documents",
        icon: <DocumentIcon />,
        children: [
          {
            title: t("navigation.allDocuments"),
            path: "/documents",
            icon: <DocumentIcon />,
          },
          {
            title: t("navigation.contracts"),
            path: "/documents/contracts",
            icon: <DocumentIcon />,
          },
          {
            title: t("navigation.templates"),
            path: "/documents/templates",
            icon: <DocumentIcon />,
          },
          {
            title: t("navigation.reports"),
            path: "/documents/reports",
            icon: <ReportIcon />,
          },
        ],
      });
      break;
  }

  return items;
};

const hasPermission = (
  role: UserContextRole,
  permission?: RolePermission,
): boolean => {
  if (!permission) return true;
  const list = RolePermissions[role] || [];
  return list.includes(permission);
};

const filterChildrenByPermission = (
  role: UserContextRole,
  children?: NavigationSubItem[],
) => {
  if (!children) return children;
  return children.filter((child) => hasPermission(role, child.permission));
};

const filterItemsByRoleAndPermission = (
  role: UserContextRole,
  items: NavigationItem[],
): NavigationItem[] => {
  return items
    .filter(
      (item) =>
        (!item.roles || item.roles.includes(role as any)) &&
        hasPermission(role, item.permission),
    )
    .map((item) => {
      const children = filterChildrenByPermission(role, item.children);
      return { ...item, ...(children !== undefined ? { children } : {}) };
    })
    .filter((item) => !item.children || item.children.length > 0);
};

const getRoleNavigationItems = (
  currentRole: UserContextRole,
  t: TFunction,
  tasksBadgeCount: number,
  notificationsBadge: number,
  chatsBadge: number,
  ordersBadge: number,
  hasAgentContract: boolean,
): NavigationItem[] => {
  const blocks = getFunctionalNavigationBlocks(
    currentRole,
    t,
    tasksBadgeCount,
    notificationsBadge,
    chatsBadge,
    ordersBadge,
    hasAgentContract,
  );
  const items: NavigationItem[] = [];

  blocks.forEach((block, index) => {
    items.push(...block.items);

    if (index < blocks.length - 1 && block.divider) {
      if (items.length > 0) {
        const last = items[items.length - 1];
        if (last != null) last.divider = true;
      }
    }
  });

  return filterItemsByRoleAndPermission(currentRole, items);
};

export const useRoleAwareNavigation = () => {
  const { currentRole } = useRole();
  const { t, i18n: _i18n } = useTranslation();
  const { badgeCount } = useTasksBadge(); // Получаем реальное количество задач из API
  const notificationsBadge = useSelector(selectUnreadCount);
  // chatsBadge и ordersBadge — пока 0 до реализации API
  const chatsBadge = 0;
  const ordersBadge = 0;
  const hasAgentContract = useHasAgentContract(
    !canSeeReferralProgram(
      convertUserRoleToContextRole(currentRole) || SystemRole.USER,
    ),
  );

  const getNavigationItems = (): NavigationItem[] => {
    const contextRole = convertUserRoleToContextRole(currentRole);
    if (currentRole && !contextRole) {
      console.warn(
        "⚠️ RoleAwareNavigation: currentRole не преобразован в contextRole",
        {
          currentRole,
          type: currentRole?.type,
          role: currentRole?.role,
        },
      );
    }
    return getRoleNavigationItems(
      contextRole || SystemRole.USER,
      t,
      badgeCount,
      notificationsBadge,
      chatsBadge,
      ordersBadge,
      hasAgentContract,
    );
  };

  const getNavigationBlocks = (): NavigationBlock[] => {
    const contextRole = convertUserRoleToContextRole(currentRole);
    return getFunctionalNavigationBlocks(
      contextRole || SystemRole.USER,
      t,
      badgeCount,
      notificationsBadge,
      chatsBadge,
      ordersBadge,
      hasAgentContract,
    );
  };

  const getCurrentRoleInfo = () => {
    // Преобразуем UserRole в UserContextRole для проверки
    const contextRole = convertUserRoleToContextRole(currentRole);

    // SUPER_ADMIN показывается отдельной меткой: иначе он неотличим от
    // PLATFORM_ADMIN — раньше обе роли схлопывались в roles.admin
    // («Администратор»). Ранний возврат перекрывает все ветки ниже.
    const isSuperAdmin =
      (currentRole?.type === "internal" &&
        currentRole.role === InternalRole.SUPER_ADMIN) ||
      contextRole === InternalRole.SUPER_ADMIN;
    if (isSuperAdmin) {
      return {
        label: t("roles.superAdmin"),
        color: "error",
        icon: <SecurityIcon />,
        description: "Полный административный доступ",
      };
    }

    // Проверяем внутренние роли (администраторы)
    if (currentRole && currentRole.type === "internal") {
      const internalRole = currentRole.role as InternalRole;
      if (
        internalRole === InternalRole.SUPER_ADMIN ||
        internalRole === InternalRole.PLATFORM_ADMIN
      ) {
        return {
          label: t("roles.admin"),
          color: "error",
          icon: <SecurityIcon />,
          description: "Администрирование системы",
        };
      }
      if (internalRole === InternalRole.PLATFORM_MANAGER) {
        return {
          label: t("roles.organizer"),
          color: "primary",
          icon: <EventIcon />,
          description: "Создание и управление мероприятиями",
        };
      }
    }

    if (isOrganizerRole(currentRole)) {
      return {
        label: t("roles.organizer"),
        color: "primary",
        icon: <EventIcon />,
        description: "Создание и управление мероприятиями",
      };
    }
    if (isSupplierRole(currentRole)) {
      return {
        label: t("roles.supplier"),
        color: "success",
        icon: <BusinessIcon />,
        description: "Предоставление услуг для мероприятий",
      };
    }
    if (isParticipantRole(currentRole)) {
      return {
        label: t("roles.participant"),
        color: "secondary",
        icon: <GroupIcon />,
        description: "Участие в мероприятиях",
      };
    }
    if (isSpeakerRole(currentRole)) {
      return {
        label: t("roles.speaker"),
        color: "warning",
        icon: <MicIcon />,
        description: "Проведение выступлений",
      };
    }
    if (isAdminRole(currentRole)) {
      return {
        label: t("roles.admin"),
        color: "error",
        icon: <SecurityIcon />,
        description: "Администрирование системы",
      };
    }

    // Если роль не определена, используем контекстную роль для определения
    if (
      contextRole &&
      Object.values(InternalRole).includes(contextRole as InternalRole)
    ) {
      const internalRole = contextRole as InternalRole;
      if (
        internalRole === InternalRole.SUPER_ADMIN ||
        internalRole === InternalRole.PLATFORM_ADMIN
      ) {
        return {
          label: t("roles.admin"),
          color: "error",
          icon: <SecurityIcon />,
          description: "Администрирование системы",
        };
      }
    }

    // Default fallback
    return {
      label: t("roles.user"),
      color: "default",
      icon: <PersonIcon />,
      description: "Стандартный доступ",
    };
  };

  return {
    getNavigationItems,
    getNavigationBlocks,
    getCurrentRoleInfo,
    currentRole,
  };
};

// Экспортируем функцию getCurrentRoleInfo для использования в других компонентах
export const getCurrentRoleInfo = (role: UserContextRole) => {
  // Проверяем внутренние роли (администраторы)
  if (Object.values(InternalRole).includes(role as InternalRole)) {
    const internalRole = role as InternalRole;
    switch (internalRole) {
      case InternalRole.SUPER_ADMIN:
      case InternalRole.PLATFORM_ADMIN:
        return {
          label: "Administrator",
          color: "error",
          icon: <SecurityIcon />,
          description: "Администрирование системы",
          permissions: [
            "admin_all",
            "user_manage",
            "system_config",
            "analytics_full",
          ],
        };
      case InternalRole.PLATFORM_MANAGER:
        return {
          label: "Platform Manager",
          color: "info",
          icon: <PersonIcon />,
          description: "Управление платформой",
          permissions: ["platform_manage", "user_moderate", "report_view"],
        };
      default:
        return {
          label: "Internal User",
          color: "default",
          icon: <PersonIcon />,
          description: "Внутренний пользователь",
          permissions: ["basic_access"],
        };
    }
  }

  switch (role) {
    case EventRole.ORGANIZER:
      return {
        label: "Organizer",
        color: "primary",
        icon: <EventIcon />,
        description: "Создание и управление мероприятиями",
        permissions: [
          "event_create",
          "event_manage",
          "team_manage",
          "finance_view",
        ],
      };
    case EventRole.SUPPLIER:
      return {
        label: "Supplier",
        color: "success",
        icon: <BusinessIcon />,
        description: "Предоставление услуг для мероприятий",
        permissions: [
          "service_create",
          "order_manage",
          "finance_view",
          "client_manage",
        ],
      };
    case EventRole.PARTICIPANT:
      return {
        label: "Participant",
        color: "secondary",
        icon: <GroupIcon />,
        description: "Участие в мероприятиях",
        permissions: ["event_join", "network_access", "schedule_view"],
      };
    case EventRole.SPEAKER:
      return {
        label: "Speaker",
        color: "warning",
        icon: <MicIcon />,
        description: "Проведение выступлений",
        permissions: [
          "presentation_manage",
          "feedback_view",
          "schedule_manage",
        ],
      };
    case SystemRole.ADMIN:
      return {
        label: "Administrator",
        color: "error",
        icon: <SecurityIcon />,
        description: "Администрирование системы",
        permissions: [
          "admin_all",
          "user_manage",
          "system_config",
          "analytics_full",
        ],
      };
    case SystemRole.MANAGER:
      return {
        label: "Manager",
        color: "info",
        icon: <PersonIcon />,
        description: "Управление платформой",
        permissions: ["platform_manage", "user_moderate", "report_view"],
      };
    case SystemRole.USER:
      return {
        label: "User",
        color: "default",
        icon: <PersonIcon />,
        description: "Базовый доступ к системе",
        permissions: ["basic_access", "profile_manage"],
      };
    case SystemRole.GUEST:
      return {
        label: "Guest",
        color: "default",
        icon: <PersonIcon />,
        description: "Ограниченный доступ",
        permissions: ["view_only"],
      };
    case OrganizerRole.MAIN_ORGANIZER:
      return {
        label: "Main Organizer",
        color: "primary",
        icon: <EventIcon />,
        description: "Полные права организатора",
        permissions: ["event_full", "team_full", "finance_full"],
      };
    case OrganizerRole.ORG_EVENT_MANAGER:
      return {
        label: "Event Manager",
        color: "primary",
        icon: <TaskIcon />,
        description: "Управление мероприятиями",
        permissions: ["event_manage", "task_manage", "team_collaborate"],
      };
    case CollaborationRole.OWNER:
      return {
        label: "Owner",
        color: "error",
        icon: <SecurityIcon />,
        description: "Полные права доступа",
        permissions: ["full_access", "permission_manage"],
      };
    case CollaborationRole.EDITOR:
      return {
        label: "Editor",
        color: "warning",
        icon: <EditIcon />,
        description: "Права редактирования",
        permissions: ["content_edit", "content_create"],
      };
    default:
      return {
        label: "User",
        color: "default",
        icon: <PersonIcon />,
        description: "Стандартный доступ",
        permissions: ["basic_access"],
      };
  }
};
